import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { clearContactCaches } from "../../api/user/clearContactCaches";
import { getContact } from "../../api/user/getContact";
import { getKeycloakUserInfo } from "../../api/user/getKeycloakUserInfo";
import { setTemporaryPassword } from "../../api/user/setTemporaryPassword";
import { setUserEnabled } from "../../api/user/setUserEnabled";
import { setUserMFA } from "../../api/user/setUserMFA";
import APAccordion from "../../components/APAccordion";
import APCheckList from "../../components/APChecklist";
import APModal from "../../components/APModal";
import APSwitch from "../../components/APSwitch";
import APButton from "../../components/buttons/APButton";
import BackButton from "../../components/buttons/BackButton";
import RefreshButton from "../../components/buttons/RefreshButton";
import SyncQuoteButton from "../../components/buttons/SyncQuoteButton";
import PageContainer from "../../components/PageContainer";
import { formatDate } from "../../helpers/dates";

const ViewKeycloakUser = () => {
  const { contactId } = useParams();
  const [tempPassword, setTempPassword] = useState(null as string | null);
  const [disableUserConfirm, setDisableUserConfirm] = useState<boolean>(false);

  if (!contactId) {
    // TODO: improve client side error messaging
    throw new Error("contact id param is misisng");
  }

  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } =
    setTemporaryPassword(contactId, {
      mutationOptions: {
        successAlertMessage: "Temporary password has been set for this user.",
      },
    });

  const { mutateAsync: mutateAsyncUpdateEnabled } = setUserEnabled(contactId, {
    mutationOptions: {
      successAlertMessage: "User enabled status has been updated.",
    },
  });

  const { mutateAsync: mutateAsyncMFAToggle } = setUserMFA(contactId);
  const { mutateAsync: mutateClearContactCaches } =
    clearContactCaches(contactId);

  const contact = getContact(contactId);
  const keycloakUser = getKeycloakUserInfo(contactId);

  if (contact.isError) {
    throw contact.error;
  }

  if (keycloakUser.isError) {
    throw keycloakUser.error;
  }

  const onReset = async () => {
    const password = await mutateAsyncUpdate();
    setTempPassword(password);
  };

  const onToggleMFA = async (enabled: boolean) => {
    mutateAsyncMFAToggle({ enabled });
  };

  const onRefresh = async () => {
    await mutateClearContactCaches();
  };

  const federatedIdentities = keycloakUser.data?.user?.federatedIdentities;
  const googleUsername = federatedIdentities?.google ?? null;
  const microsoftUsername = federatedIdentities?.microsoft ?? null;
  const appleUsername = federatedIdentities?.apple ?? null;
  const linkedinUsername = federatedIdentities?.linkedin ?? null;
  const hasFederatedIdentity =
    googleUsername ?? microsoftUsername ?? appleUsername ?? linkedinUsername;

  const isLoading =
    contact.isFetching || keycloakUser.isFetching || isLoadingUpdate;
  const isSuccess = contact.isSuccess && keycloakUser.isSuccess;

  const isEnabled = !!keycloakUser.data?.user?.enabled;
  const onToggleEnabled = async () => {
    setDisableUserConfirm(false);
    mutateAsyncUpdateEnabled({ enabled: !isEnabled });
  };

  return (
    <PageContainer loading={isLoading} title="User">
      {isSuccess && (
        <>
          <APModal
            isOpen={!!disableUserConfirm}
            onClose={() => setDisableUserConfirm(false)}
            closeText="Back"
            confirmButton={
              <APButton
                variant="contained"
                text="Yes"
                onClick={onToggleEnabled}
              />
            }
          >
            This will logout & disable all users linked to the contact. Are you
            sure?
          </APModal>
          <Stack spacing={2}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="titleSmall" color="onSurface.main">
                  {contact.data.name} - {contact.data.parentAccountEmployerName}
                </Typography>
                <Box>
                  <SyncQuoteButton contactId={contactId} />
                  <RefreshButton onClick={onRefresh} />
                </Box>
              </Box>
              <Box>
                <APCheckList
                  items={[
                    {
                      label: "Keycloak User Setup",
                      checked: !!keycloakUser.data.user,
                    },
                    {
                      label: "User Enabled",
                      checked: !!keycloakUser.data.user?.enabled,
                    },
                    {
                      label: "Email Verified",
                      checked: !!keycloakUser.data.user?.emailVerified,
                    },
                    {
                      label: "Catch-e Driver Linked to Contact",
                      checked: !!contact.data.catchEDriverId,
                    },
                    {
                      label: `User Login Success${
                        keycloakUser.data.user?.initialLoginDate
                          ? ` - ${formatDate(
                              keycloakUser.data.user.initialLoginDate,
                              "h:mm a DD/MM/YYYY"
                            )}`
                          : ""
                      }`,
                      checked: !!keycloakUser.data.user?.initialLoginDate,
                    },
                  ]}
                />
              </Box>
            </Box>
            <Box>
              <APAccordion title="How to Login?" defaultExpanded>
                <Box my={1}>
                  <Typography variant="titleMedium">
                    Single Sign On (SSO)
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="bodySmall">SSO Links</Typography>
                    <Box>
                      <APCheckList
                        items={[
                          {
                            label: `Google${
                              googleUsername ? ` - ${googleUsername}` : ""
                            }`,
                            checked: !!googleUsername,
                          },
                          {
                            label: `Microsoft${
                              microsoftUsername ? ` - ${microsoftUsername}` : ""
                            }`,
                            checked: !!microsoftUsername,
                          },
                          {
                            label: `Apple${
                              appleUsername ? ` - ${appleUsername}` : ""
                            }`,
                            checked: !!appleUsername,
                          },
                          {
                            label: `Linkedin${
                              linkedinUsername ? ` - ${linkedinUsername}` : ""
                            }`,
                            checked: !!linkedinUsername,
                          },
                        ]}
                        hideIconOnUnchecked
                      />
                    </Box>
                  </Box>
                  <Box mt={2}>
                    <Typography variant="bodySmall">
                      Emails to use to SSO register
                    </Typography>
                    <List sx={{ py: 0 }}>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText
                          primary={`• ${contact.data.email}`}
                          primaryTypographyProps={{ variant: "bodySmall" }}
                        ></ListItemText>
                      </ListItem>
                      {contact.data.email2 && (
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={`• ${contact.data.email2}`}
                            primaryTypographyProps={{ variant: "bodySmall" }}
                          ></ListItemText>
                        </ListItem>
                      )}
                      {contact.data.email3 && (
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={`• ${contact.data.email3}`}
                            primaryTypographyProps={{ variant: "bodySmall" }}
                          ></ListItemText>
                        </ListItem>
                      )}
                    </List>
                  </Box>
                </Box>
                {!hasFederatedIdentity && (
                  <>
                    <Divider sx={{ borderColor: "onSurface.main", my: 2 }} />
                    <Box>
                      <Typography variant="titleMedium">OR</Typography>
                      <Typography variant="titleMedium">
                        Username and Password
                      </Typography>
                      <Box mt={2}>
                        <Typography variant="bodySmall">
                          Username: {keycloakUser.data.user?.username}
                        </Typography>
                        {tempPassword && (
                          <Typography variant="bodySmall" sx={{ my: 1 }}>
                            Temporary Password: <b>{tempPassword}</b>
                          </Typography>
                        )}
                        <Box
                          mt={2}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Button
                            size="large"
                            color="primary"
                            variant="outlined"
                            disableElevation={true}
                            startIcon={<MailOutlineIcon />}
                            onClick={onReset}
                          >
                            Reset Password
                          </Button>
                          <Box />
                        </Box>

                        <Box mt={2}>
                          {keycloakUser.data.user && (
                            <APSwitch
                              checked={keycloakUser.data.user.mfaEnabled}
                              label="Email MFA"
                              onChange={onToggleMFA}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
              </APAccordion>
            </Box>
            <Box
              sx={{ pt: 2, display: "flex", justifyContent: "space-between" }}
            >
              <BackButton />
              <Box>
                <Button
                  size="large"
                  color={isEnabled ? "secondary" : "primary"}
                  variant="outlined"
                  disableElevation={true}
                  onClick={
                    isEnabled
                      ? () => setDisableUserConfirm(true)
                      : () => onToggleEnabled()
                  }
                >
                  {isEnabled ? "Disable" : "Enable"}
                </Button>
              </Box>
            </Box>
          </Stack>
        </>
      )}
    </PageContainer>
  );
};

export default ViewKeycloakUser;
